// Provider-priced add-ons (F-BOOT-17, part of F-HQ-07 and F-HQ-10; spec/14 §4.5, spec/25 §17, ADR-0063 decision 13).
//
//   GET   /v1/hq/addons                                                  the catalogue                       hq:subscriptions
//   POST  /v1/hq/addons                                                  offer an add-on, with its price     hq:subscriptions
//   PATCH /v1/hq/addons/{addonKey}                                       rename, reprice, stop offering      hq:subscriptions
//   GET   /v1/hq/organizations/{organizationId}/addons                   one organisation's add-ons          hq:subscriptions
//   POST  /v1/hq/organizations/{organizationId}/addons                   attach one, for N PCs               hq:subscriptions
//   PATCH /v1/hq/organizations/{organizationId}/addons/{organizationAddonId}   change the PC count, or cancel   hq:subscriptions
//   GET   /v1/organizations/{organizationId}/addons                      what the organisation pays          org:billing
//
// **An add-on** is a priced extra the Provider attaches to an organisation outside its plan. Clavian Boot is the first:
// a monthly price **per licensed PC** (`pricing: per_pc`). Attaching it grants the entitlements `boot` and
// `maxBootPcs` (its quantity), which `hq.entitlements` reads; cancelling it takes them away.
//
// **Money.** Every price is integer minor units plus an ISO 4217 code. The unit price is **snapshotted** when the
// add-on is attached: a later change to the catalogue price applies to add-ons attached after it, never to one already
// running. The monthly charge shown is `quantity × unit price`, exact (no rounding: a whole number times a whole
// number); the total is the sum of the active add-ons' charges, in the organisation's currency. Invoicing them follows
// subscription billing (F-HQ-09) — nothing here raises an invoice or takes a payment.
//
// HQ's routes are platform scope (a platform role holding `hq:subscriptions` and a second factor). Every change writes
// one audit row with its before and after: a catalogue change in the platform's log, an organisation's add-on change in
// that organisation's own log. The organisation's read is organisation scope and needs `org:billing`.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationAddonIdSchema, OrganizationIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_SUBSCRIPTIONS, ORG_BILLING } from "../permissions/index.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
// ---------------------------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------------------------
/**
 * The add-ons the platform knows how to grant. A key is code, not data: what it grants (`ADDON_GRANTS` in
 * `@clavian/domain`) is decided by a release, so the catalogue can only price and offer a key listed here.
 */
export const ADDON_KEYS = ['boot'];
export const AddonKeySchema = z.enum(ADDON_KEYS).openapi('AddonKey', {
    description: 'Which add-on. `boot`: Clavian Boot, diskless network boot for a venue’s PCs (grants `boot` and `maxBootPcs`).',
});
/** How an add-on is priced. `per_pc`: a price per licensed PC per month. */
export const ADDON_PRICINGS = ['per_pc'];
export const AddonPricingSchema = z.enum(ADDON_PRICINGS).openapi('AddonPricing', {
    description: 'How an add-on is priced. `per_pc`: its price is per licensed PC per month, and its quantity is the PC count.',
});
export const ORGANIZATION_ADDON_STATUSES = ['active', 'cancelled'];
export const OrganizationAddonStatusSchema = z.enum(ORGANIZATION_ADDON_STATUSES).openapi('OrganizationAddonStatus', {
    description: '`active`: attached, granting its entitlements and charged. `cancelled`: ended; kept as history.',
});
/** The most PCs one add-on can license. A sanity bound on input, far above any venue's floor. */
export const ADDON_MAX_QUANTITY = 10_000;
/** The PCs an add-on licenses: a whole number, at least one. */
export const AddonQuantitySchema = z.int().min(1).max(ADDON_MAX_QUANTITY);
// ---------------------------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------------------------
export const AddonSchema = z
    .object({
    key: AddonKeySchema,
    /** What it is called on screen and on an invoice: "Clavian Boot". */
    name: z.string().min(1).max(80),
    pricing: AddonPricingSchema,
    /** The price per PC per month, for add-ons attached from now on. */
    price: NonNegativeMoneySchema,
    /** False: no longer offered — it cannot be attached, and the ones already attached keep running. */
    active: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Addon', { description: 'A provider-priced add-on in the catalogue (spec/14 §4.5).' });
export const AddonListSchema = z.object({ data: z.array(AddonSchema) }).openapi('AddonList');
export const CreateAddonSchema = z
    .object({
    key: AddonKeySchema,
    name: z.string().trim().min(1).max(80),
    pricing: AddonPricingSchema,
    price: NonNegativeMoneySchema,
    /** Offered from the start unless this says otherwise. */
    active: z.boolean().optional(),
})
    .strict()
    .openapi('CreateAddon', { description: 'Offer an add-on: its key, name, pricing and price per PC per month.' });
export const UpdateAddonSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    /** The new price applies to add-ons attached after it; one already attached keeps its snapshot. */
    price: NonNegativeMoneySchema.optional(),
    active: z.boolean().optional(),
})
    .strict()
    .openapi('UpdateAddon', { description: 'Any of the name, the price and whether it is offered. A change applies from now on.' });
export const AddonParamsSchema = z.object({ addonKey: AddonKeySchema }).openapi('AddonParams');
// ---------------------------------------------------------------------------------------------
// An organisation's add-ons
// ---------------------------------------------------------------------------------------------
export const OrganizationAddonSchema = z
    .object({
    id: OrganizationAddonIdSchema,
    organizationId: OrganizationIdSchema,
    addonKey: AddonKeySchema,
    /** The catalogue name. */
    name: z.string().min(1).max(80),
    pricing: AddonPricingSchema,
    /** The PCs it licenses (`maxBootPcs` for Clavian Boot). */
    quantity: AddonQuantitySchema,
    /** The price per PC per month when it was attached. A catalogue change later does not move it. */
    unitPrice: NonNegativeMoneySchema,
    /** `quantity × unitPrice`, exact. */
    monthlyCharge: NonNegativeMoneySchema,
    status: OrganizationAddonStatusSchema,
    startedAt: IsoDateTimeSchema,
    /** When it was cancelled; null while active. */
    endedAt: IsoDateTimeSchema.nullable(),
    updatedAt: IsoDateTimeSchema,
})
    .openapi('OrganizationAddon', { description: 'An add-on attached to an organisation, with its unit price snapshot (spec/14 §4.5).' });
export const OrganizationAddonListSchema = z
    .object({
    /** The active add-ons first, then the cancelled ones, newest first within each. */
    data: z.array(OrganizationAddonSchema),
    /** The sum of the active add-ons' monthly charges, in the organisation's currency; zero when none is active. */
    monthlyTotal: NonNegativeMoneySchema,
})
    .openapi('OrganizationAddonList', { description: 'An organisation’s add-ons and what the active ones come to each month.' });
export const AttachOrganizationAddonSchema = z
    .object({
    addonKey: AddonKeySchema,
    /** The PCs to license. */
    quantity: AddonQuantitySchema,
})
    .strict()
    .openapi('AttachOrganizationAddon', { description: 'Attach an add-on at the catalogue’s current price, for this many PCs.' });
export const UpdateOrganizationAddonSchema = z
    .object({
    /** A new PC count. The unit price stays the one snapshotted at attach. */
    quantity: AddonQuantitySchema.optional(),
    /** `cancelled` ends it now: its entitlements go and it is no longer charged. It cannot be undone; attach again. */
    status: z.literal('cancelled').optional(),
})
    .strict()
    .openapi('UpdateOrganizationAddon', { description: 'Change the PC count, or cancel. One of the two, not both.' });
export const OrganizationAddonParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, organizationAddonId: OrganizationAddonIdSchema })
    .openapi('OrganizationAddonParams');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const json = (schema) => ({
    body: { content: { 'application/json': { schema } }, required: true },
});
const forbidden = problemResponse('Not HQ, no `hq:subscriptions`, or no second factor on the session.', 'forbidden', 'second-factor-required');
const failures = (extra = {}) => ({
    400: commonProblemResponses[400],
    401: commonProblemResponses[401],
    403: forbidden,
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
    ...extra,
});
const PLATFORM = 'Platform scope: requires `hq:subscriptions` and a second factor.';
export const listHqAddonsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/addons',
    operationId: 'listHqAddons',
    tags: ['hq'],
    summary: 'The add-on catalogue (F-BOOT-17)',
    description: `Every add-on the platform prices, offered or not. ${PLATFORM}`,
    [PERMISSION_EXTENSION]: HQ_SUBSCRIPTIONS,
    responses: { 200: jsonResponse(AddonListSchema, 'The catalogue.'), ...failures() },
});
export const createHqAddonRoute = createRoute({
    method: 'post',
    path: '/v1/hq/addons',
    operationId: 'createHqAddon',
    tags: ['hq'],
    summary: 'Offer an add-on, with its price (F-BOOT-17)',
    description: `Puts an add-on in the catalogue with its price per PC per month. A key already in the catalogue is \`409\`; change it with \`PATCH\`. Audited in the platform’s log. ${PLATFORM}`,
    [PERMISSION_EXTENSION]: HQ_SUBSCRIPTIONS,
    request: json(CreateAddonSchema),
    responses: {
        201: jsonResponse(AddonSchema, 'The add-on as offered.'),
        ...failures({ 409: problemResponse('The add-on is already in the catalogue.', 'conflict') }),
    },
});
export const updateHqAddonRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/addons/{addonKey}',
    operationId: 'updateHqAddon',
    tags: ['hq'],
    summary: 'Rename, reprice or stop offering an add-on (F-BOOT-17)',
    description: 'A new price applies to add-ons attached after it; one already attached keeps the unit price it was attached at. ' +
        `Not offered (\`active: false\`): it cannot be attached, and the ones attached keep running. Audited in the platform’s log, before and after. ${PLATFORM}`,
    [PERMISSION_EXTENSION]: HQ_SUBSCRIPTIONS,
    request: { params: AddonParamsSchema, ...json(UpdateAddonSchema) },
    responses: {
        200: jsonResponse(AddonSchema, 'The add-on as it is now.'),
        ...failures({ 404: problemResponse('The add-on is not in the catalogue.', 'not-found') }),
    },
});
export const listHqOrganizationAddonsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/addons',
    operationId: 'listHqOrganizationAddons',
    tags: ['hq'],
    summary: 'One organisation’s add-ons (F-BOOT-17)',
    description: `Its add-ons, active first, each with its PC count, unit price and monthly charge, and the monthly total. ${PLATFORM}`,
    [PERMISSION_EXTENSION]: HQ_SUBSCRIPTIONS,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(OrganizationAddonListSchema, 'The organisation’s add-ons.'),
        ...failures({ 404: problemResponse('No such organisation.', 'not-found') }),
    },
});
export const attachHqOrganizationAddonRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/addons',
    operationId: 'attachHqOrganizationAddon',
    tags: ['hq'],
    summary: 'Attach an add-on to an organisation, for N PCs (F-BOOT-17)',
    description: 'Attaches the add-on at the catalogue’s current price (snapshotted) for `quantity` PCs, which grants its entitlements at once. ' +
        'An add-on the organisation already has active, or one that is not offered, is `409`; one priced in another currency than the ' +
        `organisation’s is \`422 currency-mismatch\`. Audited in the organisation’s log. ${PLATFORM}`,
    [PERMISSION_EXTENSION]: HQ_SUBSCRIPTIONS,
    request: { params: OrganizationScopeParamsSchema, ...json(AttachOrganizationAddonSchema) },
    responses: {
        201: jsonResponse(OrganizationAddonSchema, 'The add-on, attached.'),
        ...failures({
            404: problemResponse('No such organisation, or the add-on is not in the catalogue.', 'not-found'),
            409: problemResponse('The organisation already has this add-on, or it is not offered.', 'conflict'),
            422: problemResponse('The add-on is priced in another currency than the organisation’s.', 'currency-mismatch'),
        }),
    },
});
export const updateHqOrganizationAddonRoute = createRoute({
    method: 'patch',
    path: '/v1/hq/organizations/{organizationId}/addons/{organizationAddonId}',
    operationId: 'updateHqOrganizationAddon',
    tags: ['hq'],
    summary: 'Change an organisation’s add-on PC count, or cancel it (F-BOOT-17)',
    description: 'Either `quantity` — the new PC count, at the unit price it was attached at — or `status: cancelled`, which ends it now and takes ' +
        `its entitlements away. A cancelled add-on cannot be changed (\`409\`). Audited in the organisation’s log, before and after. ${PLATFORM}`,
    [PERMISSION_EXTENSION]: HQ_SUBSCRIPTIONS,
    request: { params: OrganizationAddonParamsSchema, ...json(UpdateOrganizationAddonSchema) },
    responses: {
        200: jsonResponse(OrganizationAddonSchema, 'The add-on as it is now.'),
        ...failures({
            404: problemResponse('No such organisation, or no such add-on on it.', 'not-found'),
            409: problemResponse('The add-on is cancelled.', 'conflict'),
        }),
    },
});
export const listOrganizationAddonsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/addons',
    operationId: 'listOrganizationAddons',
    tags: ['organizations'],
    summary: 'What the organisation pays for add-ons (F-BOOT-17)',
    description: 'The add-ons the Provider has attached — Clavian Boot for N PCs — each with its unit price and monthly charge, and the monthly ' +
        'total. Read-only: the Provider attaches and changes them. Requires `org:billing`.',
    [PERMISSION_EXTENSION]: ORG_BILLING,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(OrganizationAddonListSchema, 'The organisation’s add-ons.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// Static paths before parameterised ones: the order the server registers them in.
export const HQ_ADDON_ROUTES = [
    listHqAddonsRoute,
    createHqAddonRoute,
    updateHqAddonRoute,
    listHqOrganizationAddonsRoute,
    attachHqOrganizationAddonRoute,
    updateHqOrganizationAddonRoute,
    listOrganizationAddonsRoute,
];
//# sourceMappingURL=hq-addons.js.map